import { OfferStatusCode } from "../../serviceOffers/models/models";

// ════════════════════════════════════════════════════════════════
//  Status message configuration for provider lifecycle stages
// ════════════════════════════════════════════════════════════════

export interface StatusMessageConfig {
  /** i18n key for the status description */
  descriptionKey: string;
  /** i18n key for the action prompt (what the user should do next) */
  actionPromptKey?: string;
  /** i18n key for what the other side is doing */
  otherSideContextKey?: string;
  /** i18n key for deadline-related labels */
  deadlineLabelKey?: string;
  /** Visual urgency level */
  urgencyLevel: "info" | "action" | "warning" | "critical";
}

/**
 * Provider-perspective status messages for all 14 offer statuses.
 * Keys are i18n paths under the "serviceOffers" namespace.
 */
export const providerStatusMessages: Record<number, StatusMessageConfig> = {
  [OfferStatusCode.draft]: {
    descriptionKey: "serviceOffers:statusMessages.draft.description",
    actionPromptKey: "serviceOffers:statusMessages.draft.actionPrompt",
    urgencyLevel: "info",
  },
  [OfferStatusCode.sent]: {
    descriptionKey: "serviceOffers:statusMessages.sent.description",
    urgencyLevel: "info",
  },
  [OfferStatusCode.viewed]: {
    descriptionKey: "serviceOffers:statusMessages.viewed.description",
    urgencyLevel: "info",
  },
  [OfferStatusCode.negotiating]: {
    descriptionKey: "serviceOffers:statusMessages.negotiating.description",
    urgencyLevel: "action",
  },
  [OfferStatusCode.accepted]: {
    descriptionKey: "serviceOffers:statusMessages.accepted.description",
    actionPromptKey: "serviceOffers:statusMessages.accepted.actionPrompt",
    urgencyLevel: "action",
  },
  [OfferStatusCode.rejected]: {
    descriptionKey: "serviceOffers:statusMessages.rejected.description",
    urgencyLevel: "info",
  },
  [OfferStatusCode.expired]: {
    descriptionKey: "serviceOffers:statusMessages.expired.description",
    urgencyLevel: "info",
  },
  [OfferStatusCode.withdrawn]: {
    descriptionKey: "serviceOffers:statusMessages.withdrawn.description",
    urgencyLevel: "info",
  },
  [OfferStatusCode.awaitingConfirmation]: {
    descriptionKey: "serviceOffers:statusMessages.awaitingConfirmation.description",
    actionPromptKey: "serviceOffers:statusMessages.awaitingConfirmation.actionPrompt",
    otherSideContextKey: "serviceOffers:statusMessages.awaitingConfirmation.otherSideContext",
    deadlineLabelKey: "serviceOffers:deadlineBar.remaining",
    urgencyLevel: "warning",
  },
  [OfferStatusCode.confirmed]: {
    descriptionKey: "serviceOffers:statusMessages.confirmed.description",
    actionPromptKey: "serviceOffers:statusMessages.confirmed.actionPrompt",
    urgencyLevel: "action",
  },
  [OfferStatusCode.workInProgress]: {
    descriptionKey: "serviceOffers:statusMessages.workInProgress.description",
    actionPromptKey: "serviceOffers:statusMessages.workInProgress.actionPrompt",
    urgencyLevel: "action",
  },
  [OfferStatusCode.workCompleted]: {
    descriptionKey: "serviceOffers:statusMessages.workCompleted.description",
    otherSideContextKey: "serviceOffers:statusMessages.workCompleted.otherSideContext",
    urgencyLevel: "info",
  },
  [OfferStatusCode.serviceCompleted]: {
    descriptionKey: "serviceOffers:statusMessages.serviceCompleted.description",
    actionPromptKey: "serviceOffers:statusMessages.serviceCompleted.actionPrompt",
    urgencyLevel: "info",
  },
  [OfferStatusCode.archived]: {
    descriptionKey: "serviceOffers:statusMessages.archived.description",
    urgencyLevel: "info",
  },
};

/**
 * Returns the status message config for a given offer status code,
 * or undefined if no config is registered.
 */
export function getProviderStatusMessage(
  statusCode: number
): StatusMessageConfig | undefined {
  return providerStatusMessages[statusCode];
}
